import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
import * as Wizard from '#~/agent/components/wizard/index.js';
import type { LinkRequest } from '#~/api/agent/index.js';
import { fetchAgent, updateAgent } from '#~/api/agent/index.js';
import type { ApiError } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { NotFoundError } from '#~/error/index.js';
import { Paths } from '#~/routes/index.js';
import { Hardware } from './hardware.jsx';
import { Problem } from './problem.jsx';
import { Anchor } from '@studio/design-system/button';
import { Input } from '@studio/design-system/input';
import { Loader } from '@studio/design-system/loader';
import T from '@studio/design-system/themes';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { unwrap } from '@studio/js-utils/async';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import { useAsyncEffect, useLatestFn } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/** `<Configure>` component. */
export const Configure = (): ReactElement => {
  const params = useParams();

  const agentId = normalizeId<Agent['id']>(params.agentId);
  const requestId = normalizeId<LinkRequest['id']>(params.requestId);

  if (!agentId || !requestId) {
    throw new NotFoundError();
  }

  const [agent, setAgent] = useState<Omit<Agent, 'lastShutDownAt' | 'status'>>();
  const [localName, setLocalName] = useState<string>();
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  useAsyncEffect(
    function* generator(signal: AbortSignal) {
      try {
        const { brandId, embedded, iconUrl, modelId, name, roomId, version } = yield* (
          unwrap(fetchAgent(agentId, yield* unwrap(getAccessTokenSilently()))) ?? {}
        );

        setAgent({ brandId, embedded, iconUrl, id: agentId, modelId, name, roomId, version });
      } catch (error_) {
        if (!signal.aborted) {
          setError(error_ as ApiError);
        }
      }
    },
    [agentId]
  );

  // Normalize the agent name.
  const canonicalName = toString(localName) ?? agent?.name ?? `${vendorName()} Studio Agent`;

  const handleUpdate = useLatestFn(async () => {
    setBusy(true);

    try {
      await updateAgent(agentId, { name: canonicalName }, await getAccessTokenSilently());

      navigate(Paths.agent.link.complete.resolve(agent!.roomId, agentId));
    } catch (error_) {
      setBusy(false);
      setError(error_ as ApiError);
    }
  });

  useRenderedBootHumanWait(!isBusy && (!!agent || !!error));

  if (!agent && !error) {
    return <Loader />;
  }

  return (
    <Wizard.Root currentStep={2} totalSteps={3}>
      {!!agent && (
        <>
          <Hardware agent={{ ...agent, name: canonicalName }} linked />
          <Wizard.Separator />
        </>
      )}

      {!!error && (
        <Problem agent={{ ...agent, id: agentId }} error={error}>
          <Anchor
            className='text-primary'
            onClick={(): void => {
              navigate(Paths.agent.link.configure.resolve(agentId, requestId));
            }}
          >
            Try again
          </Anchor>
        </Problem>
      )}

      {!!agent && !error && (
        <>
          <Wizard.Verbiage
            message='We suggest naming this machine in such a way that will allow it to be easily recognizable.'
            title={`Lastly, let's name this ${agent.embedded ? 'Camera' : 'Machine'}.`}
          />

          <Input
            data-theme={T.getTheme('light')}
            focusOnMount
            onChange={setLocalName}
            placeholder={agent.name}
            selectOnMount
            size='xl'
            value={localName}
          />

          <Wizard.ButtonBox>
            <Wizard.Button busy={isBusy} disabled={isBusy} onClick={handleUpdate}>
              {isBusy ? 'Saving...' : 'Save and Finish'}
            </Wizard.Button>

            <Anchor
              className='text-primary'
              onClick={(): void => {
                navigate(Paths.agent.resolve(agentId));
              }}
            >
              I&rsquo;ll do this later
            </Anchor>
          </Wizard.ButtonBox>
        </>
      )}
    </Wizard.Root>
  );
};
